import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { BoardItemProjection, BoardStatus, CrewProjection, DecisionProjection, RunProjection } from "../domain";
import { browserSessionStorage, hasManagerDraft, lastCrewSurface } from "../lib/crewContinuity";
import { formatDateTime, formatTime } from "../lib/time";
import { crewSurfacePath } from "../lib/routes";
import { useRemote } from "../state/RemoteContext";
import { Dialog } from "./Dialog";

export function RemoteDialogs() {
  const { dialog, setDialog, fixture } = useRemote();
  const navigate = useNavigate();
  const close = useCallback(() => setDialog(null), [setDialog]);
  if (!dialog) return null;
  const crew = fixture.crews.find((candidate) => candidate.id === dialog.crewId);
  if (!crew) return <Dialog title="Crew unavailable" onClose={close}><p>This Crew is no longer in the current remote scope.</p></Dialog>;

  if (dialog.type === "decision") {
    const decision = crew.decisions.find((candidate) => candidate.id === dialog.decisionId);
    return decision ? <DecisionDialog crew={crew} decision={decision} close={close} /> : <Dialog title="Decision unavailable" onClose={close}><p>The projection no longer contains this decision.</p></Dialog>;
  }
  if (dialog.type === "evidence") return <EvidenceDialog crew={crew} evidenceIds={dialog.evidenceIds} close={close} />;
  if (dialog.type === "run") {
    const run = crew.runs.find((candidate) => candidate.id === dialog.runId);
    return run ? <RunDialog crew={crew} run={run} action={dialog.action} close={close} /> : null;
  }
  if (dialog.type === "board") {
    const item = crew.board.find((candidate) => candidate.id === dialog.itemId);
    return item ? <BoardDialog crew={crew} item={item} close={close} /> : <Dialog title="Referenced object" onClose={close}><p>{dialog.itemId} is not a board item in the current projection.</p></Dialog>;
  }

  return (
    <Dialog title="Switch Crew" eyebrow="Context and drafts stay per Crew" onClose={close}>
      <div className="crew-switcher">
        {fixture.crews.map((candidate) => (
          <button
            type="button"
            key={candidate.id}
            className={candidate.id === crew.id ? "is-active" : ""}
            onClick={() => {
              const surface = lastCrewSurface(browserSessionStorage(), fixture.session.sessionId, candidate.id);
              navigate(crewSurfacePath(candidate.id, surface));
              close();
            }}
          >
            <span className="health-dot" data-health={candidate.health} aria-hidden="true" />
            <span><strong>{candidate.name}</strong><small>{candidate.runState}{candidate.since.decisionCount ? ` · ${candidate.since.decisionCount} decision` : ""}</small></span>
            {hasManagerDraft(browserSessionStorage(), fixture.session.sessionId, candidate.id) && <span className="draft-saved-chip">Draft saved</span>}
          </button>
        ))}
      </div>
    </Dialog>
  );
}

function DecisionDialog({ crew, decision, close }: { crew: CrewProjection; decision: DecisionProjection; close: () => void }) {
  const { can, canMutate, mutationBlockReason, actions, setDialog } = useRemote();
  const [armed, setArmed] = useState(false);
  const allowed = can("decision.respond") && canMutate;
  const canReadEvidence = can("evidence.read");
  const operation = actions.operationsByTarget[decision.id];

  useEffect(() => setArmed(false), [decision.id]);
  const approve = async () => {
    if (!armed) {
      setArmed(true);
      return;
    }
    try {
      await actions.command.mutateAsync({
        operationId: `op-${crypto.randomUUID()}`,
        action: "decision.respond",
        crewId: crew.id,
        targetId: decision.id,
        expectedRevision: decision.revision,
        payload: { response: "approve" },
      });
    } catch {
      // The mutation renders its authoritative rejection without altering the projection.
    }
  };

  return (
    <Dialog title={decision.title} eyebrow={`Decision · ${crew.shortName} · ${decision.id}`} onClose={close}>
      <p className="dialog-lede">{decision.consequence}</p>
      <dl className="provenance">
        <div><dt>Requested by</dt><dd>{decision.requestedBy}</dd></div>
        <div><dt>Requested</dt><dd>{formatDateTime(decision.requestedAt)}</dd></div>
        <div><dt>Target Run</dt><dd>{decision.runId || "Not associated"}</dd></div>
        <div><dt>Expected revision</dt><dd>{decision.revision}</dd></div>
      </dl>
      {canReadEvidence && decision.evidenceIds.length > 0 && <button type="button" className="evidence-link" onClick={() => setDialog({ type: "evidence", crewId: crew.id, evidenceIds: decision.evidenceIds })}>Review {decision.evidenceIds.length} evidence items</button>}
      {!allowed && <p className="permission-note">Approval is unavailable: {!can("decision.respond") ? "Operate access is not granted." : mutationBlockReason}</p>}
      {operation && <p className="operation-banner" data-state={operation.state} role="status">decision.respond · {operation.state} · {operation.id}</p>}
      {actions.command.isError && <p className="error-copy" role="alert">{actions.command.error instanceof Error ? actions.command.error.message : "The decision command failed."}</p>}
      <div className="dialog-actions">
        <button type="button" className="secondary-button" onClick={close}>Keep holding</button>
        <button type="button" className="attention-button" disabled={!allowed || actions.command.isPending} onClick={approve}>{armed ? "Confirm · approve extraction" : "Approve extraction"}</button>
      </div>
      {armed && <p className="confirm-help">Tap again to confirm. The manager stops again before any push.</p>}
    </Dialog>
  );
}

function EvidenceDialog({ crew, evidenceIds, close }: { crew: CrewProjection; evidenceIds: string[]; close: () => void }) {
  const { can } = useRemote();
  const evidence = crew.evidence.filter((item) => evidenceIds.includes(item.id));
  return (
    <Dialog title="Evidence" eyebrow={`${crew.shortName} · provenance`} onClose={close}>
      <div className="evidence-list">
        {!can("evidence.read") && <p>Evidence access is not granted to this device.</p>}
        {can("evidence.read") && evidence.length === 0 && <p>No bounded evidence is attached to this object.</p>}
        {can("evidence.read") && evidence.map((item) => (
          <article key={item.id}>
            <span>{item.id} · {formatTime(item.occurredAt)} · {item.actor}</span>
            <p>{item.finding}</p>
            {item.desktopOnly && <small>Full artifact available on desktop</small>}
          </article>
        ))}
      </div>
      <button type="button" className="secondary-button full-width" onClick={close}>Close evidence</button>
    </Dialog>
  );
}

function RunDialog({ crew, run, action, close }: { crew: CrewProjection; run: RunProjection; action: "pause" | "resume"; close: () => void }) {
  const { can, canMutate, mutationBlockReason, actions } = useRemote();
  const capability = action === "pause" ? "run.pause" : "run.resume";
  const allowedByRun = run.allowedActions.includes(action);
  const allowed = allowedByRun && can(capability) && canMutate;
  const operation = actions.operationsByTarget[run.id];
  const execute = async () => {
    try {
      await actions.command.mutateAsync({
        operationId: `op-${crypto.randomUUID()}`,
        action: action === "pause" ? "run.pause" : "run.resume",
        crewId: crew.id,
        targetId: run.id,
        expectedRevision: run.revision,
      });
    } catch {
      // The mutation renders its authoritative rejection without altering the projection.
    }
  };
  return (
    <Dialog title={`${action === "pause" ? "Pause" : "Resume"} ${crew.name}?`} eyebrow="Run control · confirm" onClose={close}>
      <p className="dialog-lede">{action === "pause" ? "Members finish their current step, then hold. No external effect is started while paused." : "The manager continues from the current bounded checkpoint."}</p>
      <dl className="provenance"><div><dt>Target</dt><dd>{run.id}</dd></div><div><dt>Action</dt><dd>run.{action}</dd></div><div><dt>Expected revision</dt><dd>{run.revision}</dd></div></dl>
      {!allowed && <p className="permission-note">{!allowedByRun ? "The current Run projection does not allow this action." : !can(capability) ? "This command is not granted to this device." : mutationBlockReason}</p>}
      {operation && <p className="operation-banner" data-state={operation.state} role="status">run.{action} · {operation.state} · {operation.id}</p>}
      {actions.command.isError && <p className="error-copy" role="alert">{actions.command.error instanceof Error ? actions.command.error.message : "The run command was rejected."}</p>}
      <div className="dialog-actions"><button type="button" className="secondary-button" onClick={close}>Cancel</button><button type="button" className="attention-button" onClick={execute} disabled={!allowed || actions.command.isPending}>{action === "pause" ? `Pause ${run.title}` : `Resume ${run.title}`}</button></div>
      <div className="stop-zone"><strong>Stop is not granted</strong><p>Cancel and stop require a separate desktop capability.</p><button type="button" className="danger-button" disabled>Stop Run</button></div>
    </Dialog>
  );
}

function BoardDialog({ crew, item, close }: { crew: CrewProjection; item: BoardItemProjection; close: () => void }) {
  const { can, canMutate, mutationBlockReason, actions } = useRemote();
  const [draft, setDraft] = useState(item);
  const supportedFields = item.remoteEditableFields.filter((field) => field !== "checkpoint");
  const editable = can("board.edit") && canMutate && supportedFields.length > 0;
  const canEdit = (field: BoardItemProjection["remoteEditableFields"][number]) => editable && item.remoteEditableFields.includes(field);
  const update = <K extends keyof BoardItemProjection>(key: K, value: BoardItemProjection[K]) => setDraft((current) => ({ ...current, [key]: value }));
  const save = async () => {
    try {
      await actions.editBoard.mutateAsync({
        operationId: `op-${crypto.randomUUID()}`,
        crewId: crew.id,
        boardId: item.boardId,
        cardId: item.cardId,
        goalId: item.goalId,
        expectedBoardRevision: crew.boardRevision,
        changes: {
          status: draft.status,
          priority: draft.priority,
          assigneeId: draft.assigneeId,
          operatorNote: draft.operatorNote,
        },
      });
    } catch {
      // The mutation renders its authoritative rejection without altering the projection.
    }
  };
  return (
    <Dialog title={item.title} eyebrow={`${item.id} · r${item.revision}`} onClose={close}>
      <p className="dialog-lede">{item.summary}</p>
      {!editable && <p className="permission-note">This item is read-only here. {!can("board.edit") ? "Operate access is required to edit bounded fields." : !canMutate ? mutationBlockReason : "The authority did not advertise a supported editable field."}</p>}
      <fieldset disabled={!editable || actions.editBoard.isPending} className="editor-fields">
        <legend className="visually-hidden">Board item fields</legend>
        <label>Status<select disabled={!canEdit("status")} value={draft.status} onChange={(event) => update("status", event.target.value as BoardStatus)}>{item.allowedStatusTransitions.map((status) => <option key={status} value={status}>{status}</option>)}</select></label>
        <label>Priority<select disabled={!canEdit("priority")} value={draft.priority} onChange={(event) => update("priority", event.target.value as BoardItemProjection["priority"])}><option value="urgent">Urgent</option><option value="high">High</option><option value="normal">Normal</option><option value="low">Low</option></select></label>
        <label>Assignee<select disabled={!canEdit("assignee")} value={draft.assigneeId ?? ""} onChange={(event) => update("assigneeId", event.target.value || null)}><option value="">Unassigned</option>{crew.members.filter((member) => member.role !== "manager").map((member) => <option key={member.id} value={member.id}>{member.name} · {member.health}</option>)}</select></label>
        <label>Operator note<textarea disabled={!canEdit("operatorNote")} rows={3} value={draft.operatorNote} onChange={(event) => update("operatorNote", event.target.value)} /></label>
      </fieldset>
      {actions.operationsByTarget[item.id] && <p className="operation-banner" data-state={actions.operationsByTarget[item.id].state} role="status">board.edit · {actions.operationsByTarget[item.id].state}</p>}
      {actions.editBoard.isError && <p className="error-copy" role="alert">{actions.editBoard.error instanceof Error ? actions.editBoard.error.message : "The board edit was rejected."}</p>}
      <div className="dialog-actions"><button type="button" className="secondary-button" onClick={close}>Close</button>{editable && <button type="button" className="primary-button" onClick={save} disabled={actions.editBoard.isPending}>Save changes</button>}</div>
      <p className="fine-print">Description, acceptance criteria and dependencies remain read-only · open on desktop.</p>
    </Dialog>
  );
}
